<template>
    <div class="pivot-widget">
        <div class="widget-header">
            <div class="left">
                <i
                    class="material-icons drag-handle"
                    v-tippy="{
                        placement: 'top',
                        theme: 'light',
                        arrow: true,
                        animation: 'scale-extreme',
                        duration: [200, 200],
                        hideOnClick: true,
                    }"
                    content="Sürükleyerek taşıyabilirsiniz"
                >
                    drag_indicator
                </i>
                <widget-title :widget="widget" :table="table" @widget-updated="handleWidgetUpdated" />
            </div>

            <widget-actions
                @fullscreen="handleFullscreen"
                @edit="handleEdit"
                @rename="handleRename"
                @duplicate="handleDuplicate"
                @dock="handleDock"
                @export="handleExport"
                @delete="$emit('delete', widget._id)"
                :title="widget.title"
                :widget="widget"
                :table="table"
                @widget-updated="handleWidgetUpdated"
                @widget-duplicated="handleWidgetDuplicated"
                @widget-deleted="handleWidgetDeleted"
            />
        </div>

        <div class="widget-content">
            <div v-if="!hasValidSettings" class="empty-state">
                <a-empty description="Pivot ayarları yapılmamış">
                    <a-button type="primary" @click="handleEdit">Ayarları Düzenle</a-button>
                </a-empty>
            </div>

            <div v-else-if="loading" class="loading-overlay">
                <loading-wave text="Pivot tablosu yükleniyor..." />
            </div>

            <div v-else-if="pivotData && pivotData.error" class="error-state">
                <a-empty :description="pivotData.message || 'Pivot verileri yüklenirken hata oluştu'">
                    <a-button type="primary" @click="handleEdit">Ayarları Kontrol Et</a-button>
                </a-empty>
            </div>

            <div v-else-if="!pivotData || !pivotData.rows || pivotData.rows.length === 0" class="empty-state">
                <a-empty description="Gösterilecek veri bulunamadı" />
            </div>

            <div v-else class="pivot-container">
                <pivot-table 
                    :data="pivotData" 
                    :config="widget.settings" 
                    :metadata="pivotMetadata" 
                    :value-fields="widget.settings.valueFields"
                />
            </div>
        </div>

        <pivot-settings-modal
            v-if="showSettingsModal"
            :visible="showSettingsModal"
            :widget="widget"
            :fields="table.fields"
            :table="table"
            :upstream-filters="upstreamFilters"
            @close="showSettingsModal = false"
            @save="handleSettingsSave"
        />
    </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../../../api/table";
import log2 from "../../../../../../utils/log2";
import WidgetActions from "../charts/components/WidgetActions.vue";
import WidgetTitle from "../charts/components/WidgetTitle.vue";
import PivotTable from "./components/PivotTable.vue";
import PivotSettingsModal from "./PivotSettingsModal.vue";
import LoadingWave from "../../../../../../components/common/LoadingWave.vue";

const log = log2("PivotWidget");

export default Vue.extend({
    name: "PivotWidget",
    components: {
        WidgetActions,
        WidgetTitle,
        PivotTable,
        PivotSettingsModal,
        LoadingWave,
    },
    props: {
        widget: {
            type: Object,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
        upstreamFilters: {
            type: Object,
            default: () => ({ filters: [], searchQuery: "" }),
        },
    },
    data() {
        return {
            loading: false,
            pivotData: null,
            showSettingsModal: false,
        };
    },
    computed: {
        hasValidSettings() {
            return (
                this.widget &&
                this.widget.settings &&
                this.widget.settings.rowFields &&
                this.widget.settings.rowFields.length > 0 &&
                this.widget.settings.valueFields &&
                this.widget.settings.valueFields.length > 0
            );
        },
        pivotMetadata() {
            if (!this.pivotData || !this.pivotData.metadata) {
                return { min: 0, max: 0 };
            }
            return this.pivotData.metadata;
        },
    },
    methods: {
        async loadPivotData() {
            if (!this.hasValidSettings) {
                return;
            }

            this.loading = true;
            try {
                const data = await TABLE_API.getPivotData(this.table._id, this.widget._id, this.upstreamFilters);
                this.pivotData = data;
                log("Pivot data loaded", data);
            } catch (error) {
                console.error("Pivot verileri yüklenirken hata oluştu", error);
                this.$message.error("Pivot verileri yüklenirken hata oluştu");
                this.pivotData = {
                    error: true,
                    message: "Pivot verileri yüklenirken hata oluştu",
                };
            } finally {
                this.loading = false;
            }
        },

        handleEdit() {
            this.showSettingsModal = true;
        },

        handleRename() {
            // Widget title component'i rename'i handle ediyor
        },

        async handleSettingsSave(settings) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    settings,
                });
                this.$message.success("Pivot ayarları kaydedildi");
                this.showSettingsModal = false;

                // Widget'ı güncelle
                this.widget.settings = settings;
                this.$emit("widget-updated", this.widget);

                // Pivot'ı yeniden yükle
                await this.loadPivotData();
            } catch (error) {
                console.error("Pivot ayarları kaydedilirken hata oluştu", error);
                this.$message.error("Pivot ayarları kaydedilirken hata oluştu");
            }
        },

        handleWidgetUpdated(updatedWidget) {
            this.$emit("widget-updated", updatedWidget);
        },

        async handleDuplicate() {
            try {
                const result = await TABLE_API.duplicateWidget(this.table._id, this.widget.page, this.widget._id);
                this.$message.success("Widget kopyalandı");
                this.$emit("widget-duplicated", result);
            } catch (error) {
                console.error("Widget kopyalanırken hata oluştu", error);
                this.$message.error("Widget kopyalanırken hata oluştu");
            }
        },

        handleWidgetDuplicated(result) {
            this.$emit("widget-duplicated", result);
        },

        handleWidgetDeleted() {
            this.$emit("delete", this.widget._id);
        },

        handleFullscreen() {
            // TODO: Fullscreen implementasyonu
            this.$message.info("Fullscreen yakında eklenecek");
        },

        handleDock() {
            // TODO: Dock implementasyonu
            this.$message.info("Dock yakında eklenecek");
        },

        handleExport() {
            // TODO: Export implementasyonu
            this.$message.info("Export yakında eklenecek");
        },
    },

    async mounted() {
        await this.loadPivotData();
    },

    watch: {
        upstreamFilters: {
            handler() {
                this.loadPivotData();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.pivot-widget {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #fff;

    .widget-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #e8e8e8;
        background: #fafafa;

        .left {
            display: flex;
            align-items: center;
            gap: 8px;

            .drag-handle {
                cursor: grab;
                color: #999;
                font-size: 20px;

                &:active {
                    cursor: grabbing;
                }
            }
        }
    }

    .widget-content {
        flex: 1;
        overflow: auto;
        position: relative;
        min-height: 0;

        .empty-state,
        .error-state {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            min-height: 300px;
        }

        .loading-overlay {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            min-height: 300px;
        }

        .pivot-container {
            height: 100%;
            padding: 12px;
        }
    }
}
</style>
